import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { authAPI } from "../services/api";

// ===============================
// USER TYPE
// ===============================

export type UserRole =
  | "super_admin"
  | "ministry_admin"
  | "office_admin";

export interface User {
  id: number;
  name: string;
  email: string;
  role: UserRole;
  roles?: Array<{ id?: number; name: string }>;
  department?: { id?: number; name: string };
  phone?: string | null;
  ministry_name?: string | null;
  ministry_id?: number | null;
  office_id?: number | null;
  createdAt?: string;
}

// ===============================
// AUTH STATE
// ===============================

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

type AuthAction =
  | {
      type: "LOGIN_START";
    }
  | {
      type: "LOGIN_SUCCESS";
      payload: {
        user: User;
        token: string;
      };
    }
  | {
      type: "LOGIN_FAILURE";
      payload: string;
    }
  | {
      type: "LOGOUT";
    }
  | {
      type: "LOAD_USER_SUCCESS";
      payload: User;
    }
  | {
      type: "LOAD_USER_FAILURE";
    }
  | {
      type: "UPDATE_USER";
      payload: User;
    };

// ===============================
// INITIAL STATE
// ===============================

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem("token"),
  isAuthenticated: !!localStorage.getItem("token"),
  isLoading: true,
};

// ===============================
// REDUCER
// ===============================

const authReducer = (
  state: AuthState,
  action: AuthAction
): AuthState => {
  switch (action.type) {
    case "LOGIN_START":
      return {
        ...state,
        isLoading: true,
      };

    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };

    case "LOGIN_FAILURE":
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };

    case "LOGOUT":
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };

    case "LOAD_USER_SUCCESS":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };

    case "LOAD_USER_FAILURE":
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };

    case "UPDATE_USER":
      return {
        ...state,
        user: action.payload,
      };

    default:
      return state;
  }
};

// ===============================
// CONTEXT TYPE
// ===============================

interface AuthContextType extends AuthState {
  login: (
    email: string,
    password: string
  ) => Promise<void>;

  logout: () => void;

  updateUser: (user: User) => void;

  hasRole: (role: UserRole | string) => boolean;

  hasAnyRole: (roles: string[]) => boolean;

  isAdmin: () => boolean;

  isOfficer: () => boolean;
}

// ===============================
// CONTEXT
// ===============================

const AuthContext =
  createContext<AuthContextType | undefined>(
    undefined
  );

// ===============================
// useAuth HOOK
// ===============================

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth must be used within an AuthProvider"
    );
  }

  return context;
};

// ===============================
// PROVIDER
// ===============================

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<
  AuthProviderProps
> = ({ children }) => {
  const [state, dispatch] = useReducer(
    authReducer,
    initialState
  );

  const navigate = useNavigate();

  // ===============================
  // LOAD USER FROM LOCAL STORAGE
  // ===============================

  useEffect(() => {
    const loadUser = () => {
      const token = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (!token || !storedUser) {
        dispatch({
          type: "LOAD_USER_FAILURE",
        });

        return;
      }

      try {
        const user: User =
          JSON.parse(storedUser);

        dispatch({
          type: "LOAD_USER_SUCCESS",
          payload: user,
        });
      } catch (error) {
        console.error(
          "Failed to load stored user:",
          error
        );

        localStorage.removeItem("token");
        localStorage.removeItem("user");

        dispatch({
          type: "LOAD_USER_FAILURE",
        });
      }
    };

    loadUser();
  }, []);

  // ===============================
  // LOGIN
  // ===============================

  const login = async (
    email: string,
    password: string
  ) => {
    try {
      dispatch({
        type: "LOGIN_START",
      });

      const response = await authAPI.login({
        email,
        password,
      });

      console.log(
        "LOGIN API RESPONSE:",
        response.data
      );

      const payload = response.data;

      const accessToken = payload.token;

      const apiUser = payload.user;

      if (!accessToken || !apiUser) {
        throw new Error(
          "Invalid login response from server"
        );
      }

      // ===============================
      // NORMALIZE USER
      // ===============================

      const normalizedUser: User = {
        id: Number(apiUser.id),

        name: apiUser.name,

        email: apiUser.email,

        role: apiUser.role as UserRole,

        phone: apiUser.phone ?? null,

        ministry_name:
          apiUser.ministry_name ?? null,

        ministry_id:
          apiUser.ministry_id ?? null,

        office_id:
          apiUser.office_id ?? null,

        createdAt:
          apiUser.createdAt ??
          new Date().toISOString(),
      };

      // ===============================
      // SAVE AUTH DATA
      // ===============================

      localStorage.setItem(
        "token",
        accessToken
      );

      localStorage.setItem(
        "user",
        JSON.stringify(normalizedUser)
      );

      // ===============================
      // UPDATE STATE
      // ===============================

      dispatch({
        type: "LOGIN_SUCCESS",

        payload: {
          user: normalizedUser,
          token: accessToken,
        },
      });

      toast.success(
        "Login successful!"
      );

      // ===============================
      // ROLE-BASED REDIRECT
      // ===============================

      switch (normalizedUser.role) {
        case "super_admin":
          navigate(
            "/super-admin/dashboard"
          );
          break;

        case "ministry_admin":
          navigate(
            "/ministry/dashboard"
          );
          break;

        case "office_admin":
          navigate(
            "/office/dashboard"
          );
          break;

        default:
          navigate("/");
          break;
      }
    } catch (error: any) {
      console.error(
        "Login error:",
        error
      );

      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Login failed";

      dispatch({
        type: "LOGIN_FAILURE",
        payload: message,
      });

      throw new Error(message);
    }
  };

  // ===============================
  // LOGOUT
  // ===============================

  const logout = () => {
    localStorage.removeItem("token");

    localStorage.removeItem("user");

    dispatch({
      type: "LOGOUT",
    });

    toast.success(
      "Logged out successfully"
    );

    navigate("/");
  };

  // ===============================
  // UPDATE USER
  // ===============================

  const updateUser = (user: User) => {
    localStorage.setItem(
      "user",
      JSON.stringify(user)
    );

    dispatch({
      type: "UPDATE_USER",
      payload: user,
    });
  };

  // ===============================
  // ROLE CHECK
  // ===============================

  const hasRole = (
    role: UserRole | string
  ): boolean => {
    return (
      state.user?.role?.toLowerCase() ===
      role.toLowerCase()
    );
  };

  // ===============================
  // MULTIPLE ROLE CHECK
  // ===============================

  const hasAnyRole = (
    roles: string[]
  ): boolean => {
    if (!state.user?.role) {
      return false;
    }

    return roles.some(
      (role) =>
        role.toLowerCase() ===
        state.user!.role.toLowerCase()
    );
  };

  // ===============================
  // ADMIN CHECK
  // ===============================

  const isAdmin = () => {
    return (
      state.user?.role ===
        "super_admin" ||
      state.user?.role ===
        "ministry_admin" ||
      state.user?.role ===
        "office_admin"
    );
  };

  // ===============================
  // OFFICER CHECK
  // ===============================

  const isOfficer = () => {
    return false;
  };

  // ===============================
  // CONTEXT VALUE
  // ===============================

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    updateUser,
    hasRole,
    hasAnyRole,
    isAdmin,
    isOfficer,
  };

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
};